<template>
  <div class="h-dvh overflow-hidden flex flex-col text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900">

    <HeaderBar @help="handleHelp"/>

    <main class="flex-1 min-h-0 overflow-y-auto flex flex-col w-full mx-auto max-w-[2440px] overflow-x-hidden px-4 pb-8 pt-4 sm:px-6 lg:px-8">
      <div v-if="activeTab === 'convert'" key="convert" class="tab-pane">

        <div class="grid gap-6 lg:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)] lg:items-stretch xl:grid-cols-[minmax(22rem,26rem)_minmax(0,1fr)]">

          <!-- Linke Sidebar -->
          <div class="min-w-0 space-y-4 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(100dvh-var(--header-h)-3rem)] lg:overflow-y-auto">
            <UploadCard @file-removed="resetWorkingFile" :variant="isHavingFile?'compact':'full' "/>
            <div class="flex flex-col gap-4 sm:flex-row sm:flex-wrap lg:flex-col">
              <SettingsBasic v-if="isHavingFile " class="flex  min-h-0  flex-col  shrink grow-1" v-model:expanded="settingsExpanded"/>

              <div v-if="isHavingFile " id="transposition-panel" class="flex   min-h-0 flex-col  grow-1 animate-fade-left animate-once animate-duration-500 animate-ease-in">
                <TranspositionTabs v-model:is-open="transposeOpen"/>
              </div>

            </div>
          </div>

          <!-- Rechte Hauptfläche: Modus + Stimmen + Preview + Download -->
          <div class="min-w-0 space-y-4 lg:border-l lg:border-gray-200 lg:pl-6 flex flex-col">
            <PartSelection/>
            <ConvertPanel v-if="isHavingFile"/>
            <MeasureSelectionView v-if="mode === 'per_measure'"/>
            <PreviewPane/>
            <DownloadPanel :visible="!!previewFileContent" @new-conversion="resetWorkingFile"/>
          </div>

        </div>
      </div>

      <div v-else-if="activeTab === 'history'" key="history" class="tab-pane">
        <FileHistoryPanel/>
      </div>

      <div v-else-if="activeTab === 'keyboard'" key="keyboard" class="tab-pane">
        <KeyboardView/>
      </div>

      <div v-else-if="activeTab === 'settings'" key="settings" class="tab-pane mx-auto max-w-4xl">
        <SettingsPanel/>
      </div>
    </main>

    <GlobalNotifications/>
    <MovementNoticeDialog/>

    <!-- Datei-Drop-Overlay für das gesamte Fenster. pointer-events-none ist kritisch: Drops müssen
         zu den darunterliegenden Elementen (UploadCard / window-Handler) durchfallen. -->
    <div
        v-if="isDraggingFile"
        class="fixed inset-0 z-40 pointer-events-none flex flex-col items-center justify-center border-4 border-dashed border-sky-400 bg-sky-500/10 backdrop-blur-sm"
    >
      <Icon icon="fa6-solid:cloud-arrow-up" class="text-6xl text-sky-500 mb-3"/>
      <p class="text-lg font-semibold text-sky-700">Datei hier ablegen zum Hochladen</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {storeToRefs} from 'pinia';
import {useEventListener} from '@vueuse/core';
import {Icon} from '@iconify/vue';
import HeaderBar from '@/components/HeaderBar.vue';
import UploadCard from '@/components/UploadCard.vue';
import SettingsPanel from '@/components/settings/SettingsPanel.vue';
import PreviewPane from '@/components/PreviewPane.vue';
import DownloadPanel from '@/components/DownloadPanel.vue';
import MeasureSelectionView from '@/components/measure-selection/MeasureSelectionView.vue';
import TranspositionTabs from '@/components/transposition/TranspositionTabs.vue';
import SettingsBasic from "@/components/settings/SettingsBasic.vue";
import PartSelection from "@/components/PartSelection.vue";
import ConvertPanel from "@/components/ConvertPanel.vue";
import FileHistoryPanel from "@/components/file-history/FileHistoryPanel.vue";
import KeyboardView from "@/features/keyboard/KeyboardView.vue";
import GlobalNotifications from "@/components/GlobalNotifications.vue";
import MovementNoticeDialog from "@/components/movement-tabs/MovementNoticeDialog.vue";
import LogRocket from 'logrocket';
import {useSettingsStore} from '@/stores/settings';
import {useNotificationStore} from '@/stores/notifications';
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import {useTabStore} from "@/stores/useTabStore.ts";
import {useFileHistoryStore} from "@/stores/useFileHistoryStore";
import {useHistoryCapture} from "@/composables/useHistoryCapture";
import {useIsMobile} from "@/composables/useIsMobile";
import {createWindowFileDragState} from "@/shared/windowFileDrag";

const settingsStore = useSettingsStore();
const notificationStore = useNotificationStore();
const conversionStore = useMusicUploadStore();

const {mode} = storeToRefs(settingsStore);
const {isHavingFile, previewFileContent} = storeToRefs(conversionStore);
const {activeMainTab: activeTab} = storeToRefs(useTabStore());


// Datei-Drop im GESAMTEN Browser-Fenster → Upload (nicht nur die UploadCard-Dropzone). Ein Drop
// irgendwo lädt die Datei hoch; auf anderen Tabs wird dafür zu "Konvertieren" gewechselt. Der
// window-Handler ist konfliktfrei: UploadCard.onDrop ruft stopPropagation() (erreicht window nicht,
// kein Doppel-Upload).
const isDraggingFile = ref(false);

// Kritisch: preventDefault() NUR bei ECHTEN Datei-Drags. Unbedingtes preventDefault würde sonst
// auch das native Text-Drag&Drop in Eingabefelder / den MusicXML-Quelltext-Editor annullieren.
// "Echt" heißt: von außerhalb des Browsers – In-Page-Drags (z. B. ein gezogenes Bild) melden
// ebenfalls `types: ['Files']` und werden über die dragstart-Flagge ausgeschlossen (Warum im
// Docstring von shared/windowFileDrag.ts).
const fileDrag = createWindowFileDragState();

function onWindowDragOver(event: DragEvent) {
  if (!fileDrag.isExternalFileDrag(event)) return;
  event.preventDefault(); // Pflicht, damit `drop` überhaupt feuert (unterdrückt Browser-Default).
  isDraggingFile.value = true;
}

function onWindowDragLeave(event: DragEvent) {
  // Nur ausblenden, wenn der Cursor das Fenster wirklich verlässt (kein Flackern über Kinder).
  if (event.relatedTarget === null) isDraggingFile.value = false;
}

async function onWindowDrop(event: DragEvent) {
  if (!fileDrag.isExternalFileDrag(event)) return;
  event.preventDefault();
  isDraggingFile.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  if (activeTab.value !== 'convert') activeTab.value = 'convert';
  try {
    await conversionStore.updateAndAnalyzeFile(file);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    notificationStore.notify(`Fehler beim Hochladen der Datei: ${message}`, 'error', 4000);
  }
}

useEventListener(window, 'dragenter', onWindowDragOver);
useEventListener(window, 'dragover', onWindowDragOver);
useEventListener(window, 'dragleave', onWindowDragLeave);
useEventListener(window, 'drop', onWindowDrop);
// In-Page-Drags erkennen: `dragstart` feuert nur für Drags, die IM Dokument beginnen (eine von
// außen gezogene Datei nie) – solche Drags dürfen weder Overlay noch Upload auslösen.
useEventListener(window, 'dragstart', (event: DragEvent) => fileDrag.onDragStart(event));
// `dragend` feuert auch bei abgebrochenem Drag (Esc / außerhalb des Fensters losgelassen), wo
// weder `drop` noch ein `dragleave` mit relatedTarget === null garantiert sind → Overlay weg.
useEventListener(window, 'dragend', () => {
  fileDrag.reset();
  isDraggingFile.value = false;
});
// Sicherheitsnetz: jede neue Mausgeste setzt den In-Page-Merker zurück (siehe windowFileDrag.ts).
useEventListener(window, 'pointerdown', () => fileDrag.reset());
// Das Overlay IMMER ausblenden, sobald irgendwo im Fenster gedroppt wird – auch wenn ein
// tieferliegender Handler (UploadCard.onDrop) das Bubbling per stopPropagation() stoppt und
// onWindowDrop (Bubble-Phase) daher nie erreicht. Die Capture-Phase läuft VOR der Target-/
// Bubble-Phase, also vor dem stopPropagation → das Flag wird zuverlässig zurückgesetzt. Der
// eigentliche Upload bleibt in onWindowDrop (Bubble) und wird von der UploadCard weiter
// blockiert → kein Doppel-Upload.
useEventListener(window, 'drop', () => {
  isDraggingFile.value = false;
  fileDrag.reset();
}, {capture: true});

// Auf mobilen Viewports (< md) starten Einstellungen + Transpositionen eingeklappt. Der
// Initialwert wird einmalig erfasst (nicht reaktiv an isMobile gebunden), damit manuelles
// Auf-/Zuklappen erhalten bleibt und ein Resize die Panels nicht erneut zuklappt.
const isMobile = useIsMobile();
const settingsExpanded = ref(false);
const transposeOpen = ref(!isMobile.value);
const isLocalDev = import.meta.env.DEV;

// Dateihistorie: Capture-Watcher starten und persistierte Einträge laden (tab-unabhängig).
useHistoryCapture();
const fileHistoryStore = useFileHistoryStore();
void (async () => {
  await fileHistoryStore.hydrate();
  // `?reuse=<id>` (von "In neuem Tab öffnen") hat Vorrang; sonst Arbeitszustand aus der URL
  // (?file=...&semitones=...&lineage=...) rekonstruieren – kein localStorage mehr.
  const reuseId = conversionStore.takeReuseId();
  if (reuseId) {
    await fileHistoryStore.reuseById(reuseId);
  } else if (conversionStore.currentFileId) {
    // Stateless: zuletzt bearbeiteten Stand aus der lokalen History wiederherstellen (der Server
    // speichert nichts). Schlägt das fehl (kein Eintrag), den nutzlosen URL-Zustand verwerfen.
    const restored = await fileHistoryStore.restoreCurrentFromHistory();
    if (!restored) await conversionStore.initFromUrl();
  }
})();

function handleHelp() {
  notificationStore.notify('Weitere Hilfe ist derzeit nicht verfügbar.', 'info', 4000);
}

// Ein Handler für beide Wege zum Zurücksetzen der Arbeitsdatei (UploadCard „Datei entfernen",
// DownloadPanel „Erneut konvertieren").
function resetWorkingFile() {
  conversionStore.reset();
}

onMounted(() => {
  if (!isLocalDev)
    LogRocket.init('dli1jz/griffschrift');
})
</script>

<style>
@import 'tailwindcss';

/* Robuste Tab-Einblendung: reine CSS-Animation beim Mounten (v-if) – läuft immer bis zum
   Endzustand (opacity:1) durch und kann nicht wie eine JS-koordinierte <Transition> hängen
   bleiben. Ersetzt die zuvor fragile <Transition mode="out-in">. */
.tab-pane {
  animation: tab-pane-in 0.25s ease-out;
  @apply flex-1 flex flex-col;
}

@keyframes tab-pane-in {
  from {
    opacity: 0;
    transform: translateY(0.75rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
